// Runtime application of a BrandTheme to the document.
// Idempotent: applying twice produces the same result. Replaces previous theme.

import type { BrandTheme, BrandThemeMode, ModeRequest, ThemeOverrides } from "./types";

const FONT_LINK_ID = "gz-brand-theme-font";

// Map our colors-key to the CSS var names consumed by @theme inline in
// packages/ui/src/styles/globals.css.
const COLOR_VAR_MAP: Record<keyof BrandThemeMode["colors"], string> = {
  primary: "--primary",
  "primary-foreground": "--primary-foreground",
  secondary: "--secondary",
  "secondary-foreground": "--secondary-foreground",
  accent: "--accent",
  "accent-foreground": "--accent-foreground",
  background: "--background",
  foreground: "--foreground",
  muted: "--muted",
  "muted-foreground": "--muted-foreground",
  border: "--border",
};

// ── Mode detection ────────────────────────────────────────────────────────

export function isDarkMode(mode: BrandThemeMode | undefined): boolean {
  if (!mode) return false;
  const bg = mode.colors.background;
  if (!bg) return false;
  return parseLightness(bg) < 50;
}

// Extract the lightness % from an HSL string, or compute relative luminance
// for hex. Returns 0-100. Defaults to 50 if not parseable (no-op for our use).
function parseLightness(color: string): number {
  const hslMatch = color.match(/hsl\(\s*[\d.]+\s*,\s*[\d.]+%\s*,\s*([\d.]+)%/i);
  const hslLightness = hslMatch?.[1];
  if (hslLightness) return parseFloat(hslLightness);
  const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  const hexCapture = hexMatch?.[1];
  if (hexCapture) {
    const hex = hexCapture.length === 3 ? hexCapture.split("").map((c) => c + c).join("") : hexCapture;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return ((r + g + b) / 3 / 255) * 100;
  }
  return 50;
}

// Resolve a ModeRequest to a concrete mode the theme actually supports.
// "auto" matches the document's current dark/light class (set by next-themes).
// If the brand doesn't have the requested mode, falls back to defaultMode.
export function resolveMode(theme: BrandTheme, requested: ModeRequest = "auto"): "light" | "dark" {
  let wanted: "light" | "dark";
  if (requested === "auto") {
    const isDarkNow =
      typeof document !== "undefined" && document.documentElement.classList.contains("dark");
    wanted = isDarkNow ? "dark" : "light";
  } else {
    wanted = requested;
  }
  if (theme.modes[wanted]) return wanted;
  return theme.defaultMode;
}

// ── Surface derivation ────────────────────────────────────────────────────
// When the brand doesn't define --card etc, derive sensible values from the
// brand's --background and --foreground so the surface palette stays coherent.

interface HSL {
  h: number;
  s: number;
  l: number;
}

function parseHSL(color: string): HSL | null {
  const m = color.match(/hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/i);
  if (!m) return null;
  const h = m[1];
  const s = m[2];
  const l = m[3];
  if (!h || !s || !l) return null;
  return { h: parseFloat(h), s: parseFloat(s), l: parseFloat(l) };
}

function formatHSL(hsl: HSL): string {
  return `hsl(${hsl.h.toFixed(0)}, ${hsl.s.toFixed(0)}%, ${hsl.l.toFixed(1)}%)`;
}

// Shift lightness toward (or away from) the foreground by `amount` percentage
// points. For dark themes, foreground is light, so cards lighten slightly
// from background. For light themes, cards darken slightly. Either way the
// surface gets a subtle visual lift without losing the brand's mode.
function shiftTowardFg(bg: HSL, fg: HSL, amount: number): HSL {
  const dir = fg.l > bg.l ? 1 : -1;
  return {
    h: bg.h,
    s: bg.s,
    l: Math.max(0, Math.min(100, bg.l + dir * amount)),
  };
}

// Derive missing surface vars from background + foreground anchors.
// Returns a Record of CSS var names → values; only used to fill values that
// the brand didn't define explicitly (or that build-themes cleared due to
// mode-mismatch). The caller skips assignment for any var already present.
function deriveSurfaceVars(bg: string | undefined, fg: string | undefined): Record<string, string> {
  if (!bg || !fg) return {};
  const bgHSL = parseHSL(bg);
  const fgHSL = parseHSL(fg);
  if (!bgHSL || !fgHSL) return {};

  const card = formatHSL(shiftTowardFg(bgHSL, fgHSL, 4));
  const popover = formatHSL(shiftTowardFg(bgHSL, fgHSL, 6));
  const input = formatHSL(shiftTowardFg(bgHSL, fgHSL, 8));

  // --muted: a faint variant of background, slightly shifted toward fg.
  // --muted-foreground: a dimmer variant of foreground, shifted toward bg.
  // These match shadcn semantics: muted is "background lite", muted-fg is
  // "foreground lite" — the readable-but-quieter pair. Build-themes clears
  // designlang's --muted when it lands on the wrong side of the lightness
  // axis (Payload's near-black footer band → light-mode --muted), so this
  // fallback is what gets used in those cases.
  const muted = formatHSL(shiftTowardFg(bgHSL, fgHSL, 5));
  const mutedFg = formatHSL(shiftTowardFg(fgHSL, bgHSL, 30));

  return {
    "--card": card,
    "--card-foreground": fg,
    "--popover": popover,
    "--popover-foreground": fg,
    "--input": input,
    "--muted": muted,
    "--muted-foreground": mutedFg,
  };
}

// ── Apply / clear ─────────────────────────────────────────────────────────

export interface ApplyThemeOptions {
  mode?: ModeRequest;
}

export function applyTheme(theme: BrandTheme, overrides: ThemeOverrides = {}, opts: ApplyThemeOptions = {}): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  const mode = resolveMode(theme, opts.mode ?? "auto");
  const modeData = theme.modes[mode];
  if (!modeData) return; // shouldn't happen — defaultMode should always be present

  // Clear previously-applied vars before laying down new ones. Without this,
  // switching from a brand that defines --background to one that doesn't
  // leaves the previous --background in place, producing chimeras like
  // Firecrawl's primary orange on Linear's dark background.
  const previousVars = root.getAttribute("data-brand-theme-vars");
  if (previousVars) {
    for (const name of previousVars.split(",").filter(Boolean)) {
      root.style.removeProperty(name);
    }
  }

  // The light/dark class on <html> is owned by next-themes — the brand
  // provider passes resolvedTheme as the explicit mode here, so our chosen
  // per-mode bucket already aligns with the class next-themes set. If the
  // brand has only one mode and the user picked the other via next-themes,
  // resolveMode() falls back to defaultMode and we apply the brand vars we
  // do have; the codebase's `.dark` block still fires for un-overridden
  // vars (chart-*, sidebar, etc) because next-themes' class stays set.
  // Writing the class here would just stomp on next-themes.

  // Track applied vars so the next applyTheme / clearTheme can remove them.
  // Stash the list on a data attribute so we don't need theme state during
  // clear.
  const appliedNames: string[] = [];
  const set = (name: string, value: string) => {
    root.style.setProperty(name, value);
    if (!appliedNames.includes(name)) appliedNames.push(name);
  };

  // 1. Per-mode named colors
  for (const [key, value] of Object.entries(modeData.colors)) {
    if (!value) continue;
    const varName = COLOR_VAR_MAP[key as keyof BrandThemeMode["colors"]];
    if (varName) set(varName, value);
  }

  // 2. Mode-shared design tokens
  if (theme.radius) set("--radius", theme.radius);
  if (theme.fonts.body) {
    set("--font-sans", `${theme.fonts.body.name}, ${theme.fonts.body.fallback}`);
  }
  if (theme.fonts.mono) {
    set("--font-mono", `${theme.fonts.mono.name}, ${theme.fonts.mono.fallback}`);
  }

  // 3. Per-mode allVars dump (anything from the site's variables.css that
  //    we didn't already cover). These can be brand-specific globals like
  //    --rhythm-* or --stack-* (build-themes folds those in).
  for (const [name, value] of Object.entries(modeData.allVars)) {
    if (!appliedNames.includes(name)) set(name, value);
  }

  // 4. Derived surface vars — fill any --card/--popover/--input that the
  //    brand didn't define explicitly. Without this, cards inherit
  //    next-themes' light-mode value while the brand renders dark.
  const derived = deriveSurfaceVars(modeData.colors.background, modeData.colors.foreground);
  for (const [name, value] of Object.entries(derived)) {
    if (!appliedNames.includes(name)) set(name, value);
  }

  // 5. Per-app overrides last so they win
  for (const [name, value] of Object.entries(overrides)) {
    set(name, value);
  }

  root.setAttribute("data-brand-theme", theme.slug);
  root.setAttribute("data-brand-theme-mode", mode);
  root.setAttribute("data-brand-theme-vars", appliedNames.join(","));

  ensureFontLinks(theme);
}

export function clearTheme(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  const previous = root.getAttribute("data-brand-theme-vars");
  if (previous) {
    for (const name of previous.split(",").filter(Boolean)) {
      root.style.removeProperty(name);
    }
  }
  root.removeAttribute("data-brand-theme");
  root.removeAttribute("data-brand-theme-mode");
  root.removeAttribute("data-brand-theme-vars");

  // We don't strip the .dark / .light class here — next-themes owns that.
  // It will re-assert its own choice on next render.

  const link = document.getElementById(FONT_LINK_ID);
  if (link) link.remove();
  document.querySelectorAll(`link[data-${FONT_LINK_ID}-extra]`).forEach((el) => {
    el.remove();
  });
}

// ── Font loading ──────────────────────────────────────────────────────────

function ensureFontLinks(theme: BrandTheme): void {
  const urls = [
    theme.fonts.body?.googleFontUrl,
    theme.fonts.heading?.googleFontUrl,
    theme.fonts.mono?.googleFontUrl,
  ].filter((u): u is string => Boolean(u));

  let link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;

  if (urls.length === 0) {
    if (link) link.remove();
    return;
  }

  const uniq = Array.from(new Set(urls));
  const primary = uniq[0];
  if (!primary) return;
  if (!link) {
    link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = primary;

  document.querySelectorAll(`link[data-${FONT_LINK_ID}-extra]`).forEach((el) => {
    el.remove();
  });
  for (let i = 1; i < uniq.length; i++) {
    const extraHref = uniq[i];
    if (!extraHref) continue;
    const extra = document.createElement("link");
    extra.rel = "stylesheet";
    extra.href = extraHref;
    extra.setAttribute(`data-${FONT_LINK_ID}-extra`, String(i));
    document.head.appendChild(extra);
  }
}

// ── SSR style tag (for static-export apps that can't run JS pre-paint) ────

export interface RenderStyleOptions {
  mode?: ModeRequest;
}

export function renderThemeStyleTag(
  theme: BrandTheme,
  overrides: ThemeOverrides = {},
  opts: RenderStyleOptions = {},
): string {
  // SSR can't read the document's class. Caller must pass the mode explicitly
  // or we use defaultMode. "auto" without document context falls to default.
  let mode: "light" | "dark";
  if (opts.mode === "light" || opts.mode === "dark") {
    mode = theme.modes[opts.mode] ? opts.mode : theme.defaultMode;
  } else {
    mode = theme.defaultMode;
  }
  const modeData = theme.modes[mode];
  if (!modeData) return "";

  // Track names so derived vars only fill gaps. Mirrors applyTheme's
  // ordering — brand-set values win over our derivations.
  const lines: string[] = [];
  const emitted = new Set<string>();
  const emit = (name: string, value: string) => {
    if (emitted.has(name)) return;
    lines.push(`${name}:${value};`);
    emitted.add(name);
  };

  for (const [key, value] of Object.entries(modeData.colors)) {
    if (!value) continue;
    const varName = COLOR_VAR_MAP[key as keyof BrandThemeMode["colors"]];
    if (varName) emit(varName, value);
  }
  if (theme.radius) emit("--radius", theme.radius);
  if (theme.fonts.body) emit("--font-sans", `${theme.fonts.body.name},${theme.fonts.body.fallback}`);
  if (theme.fonts.mono) emit("--font-mono", `${theme.fonts.mono.name},${theme.fonts.mono.fallback}`);
  for (const [name, value] of Object.entries(modeData.allVars)) {
    emit(name, value);
  }
  const derived = deriveSurfaceVars(modeData.colors.background, modeData.colors.foreground);
  for (const [name, value] of Object.entries(derived)) {
    emit(name, value);
  }
  // Per-app overrides win regardless — push without dedup.
  for (const [name, value] of Object.entries(overrides)) {
    lines.push(`${name}:${value};`);
  }

  return `:root{${lines.join("")}}`;
}

// Backwards-compat: existing callers may not pass options. Default to "auto"
// at runtime (read class) and defaultMode at SSR.
